import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import ProgressBar from '../components/ProgressBar';
import EmptyState from '../components/EmptyState';
import { Plus, Search, Eye, Edit, Archive } from 'lucide-react';
import { calculateProgress } from '../utils/helpers';

const ObjetivosPage = () => {
  const navigate = useNavigate();
  const { user, workspace, selectedPeriod, setSelectedPeriod, periods } = useAuth();
  const [loading, setLoading] = useState(true);
  const [objectives, setObjectives] = useState([]);
  const [filteredObjectives, setFilteredObjectives] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (workspace) {
      loadObjectives();
    }
  }, [workspace, selectedPeriod]);

  useEffect(() => {
    filterObjectives();
  }, [objectives, searchTerm, statusFilter]);

  const [error, setError] = useState(null);

  const loadObjectives = async () => {
    // Master admin without workspace should see different message
    if (user?.role === 'master_admin' && !workspace?.id) {
      setError('Como master admin, selecciona un workspace en la página de Workspaces para ver sus objetivos.');
      setLoading(false);
      return;
    }

    if (!workspace?.id) {
      setError('No tienes un workspace asignado. Contacta al administrador.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = {
        period: selectedPeriod || undefined,
      };

      const data = await api.get(`/workspaces/${workspace.id}/objectives`, { params });
      setObjectives(data || []);
    } catch (err) {
      console.error('Load objetivos error:', err);
      setError(err.error || err.message || 'Error al cargar objetivos');
      setObjectives([]);
    } finally {
      setLoading(false);
    }
  };

  const filterObjectives = () => {
    let filtered = [...objectives];

    if (searchTerm) {
      filtered = filtered.filter((obj) =>
        obj.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((obj) => obj.status === statusFilter);
    }

    setFilteredObjectives(filtered);
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-700">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 text-red-600 underline"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-astra-secondary-500">Objetivos</h1>
        <Button onClick={() => navigate('/objetivos/nuevo')} icon={Plus}>
          Nuevo Objetivo
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Input
              placeholder="Buscar por descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={Search}
            />
          </div>
          <Select
            value={selectedPeriod || ''}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            options={periods.map((p) => ({
              value: p.period,
              label: p.period,
            }))}
            placeholder="Período"
          />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: 'all', label: 'Todos los estados' },
              { value: 'active', label: 'Activo' },
              { value: 'completed', label: 'Completado' },
              { value: 'archived', label: 'Archivado' },
            ]}
          />
        </div>
      </div>

      {/* Table */}
      {filteredObjectives.length === 0 ? (
        <EmptyState
          title="No hay objetivos"
          description="Comienza creando tu primer objetivo para este período"
          actionLabel="Crear Objetivo"
          onAction={() => navigate('/objetivos/nuevo')}
        />
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-astra-secondary-500 uppercase tracking-wider">
                    Período
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-astra-secondary-500 uppercase tracking-wider">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-astra-secondary-500 uppercase tracking-wider">
                    # KRs
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-astra-secondary-500 uppercase tracking-wider">
                    Progreso
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-astra-secondary-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-astra-secondary-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredObjectives.map((objective) => {
                  const progress = parseFloat(objective.avg_progress || 0);
                  return (
                    <tr
                      key={objective.id}
                      onClick={() => navigate(`/objetivos/${objective.id}`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-astra-secondary-500">
                        {objective.period}
                      </td>
                      <td className="px-6 py-4 text-sm text-astra-secondary-500">
                        {objective.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-astra-accent-500">
                        {objective.key_results_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-indigo-600 h-2 rounded-full"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">
                            {progress.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={objective.status}
                          onChange={async (e) => {
                            e.stopPropagation();
                            try {
                              await api.put(`/objectives/${objective.id}`, {
                                status: e.target.value
                              });
                              loadObjectives();
                            } catch (error) {
                              console.error('Error updating status:', error);
                              alert('Error al actualizar el estado');
                            }
                          }}
                          className="px-3 py-1 rounded-full text-sm font-semibold border"
                          style={{
                            backgroundColor: 
                              objective.status === 'active' ? '#dcfce7' :
                              objective.status === 'completed' ? '#dbeafe' :
                              objective.status === 'archived' ? '#f3f4f6' :
                              '#fef3c7',
                            color:
                              objective.status === 'active' ? '#166534' :
                              objective.status === 'completed' ? '#1e40af' :
                              objective.status === 'archived' ? '#374151' :
                              '#92400e'
                          }}
                        >
                          <option value="active">Activo</option>
                          <option value="completed">Completado</option>
                          <option value="on_hold">En Pausa</option>
                          <option value="archived">Archivado</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/objetivos/${objective.id}`);
                            }}
                            className="text-astra-primary-500 hover:text-astra-primary-700"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObjetivosPage;
